import React, { useEffect, useState } from 'react';
import { apiGet, apiPut } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { RefreshCw, RotateCcw } from 'lucide-react';

export default function AdminRSVP() {
  const [rsvps, setRsvps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');

  const load = () => {
    apiGet('/api/rsvp').then(r => setRsvps(r.data.rsvps || [])).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const handleReopen = async (guestId) => {
    if (!window.confirm('Genåbn dette RSVP, så gæsten kan svare igen?')) return;
    try {
      await apiPut(`/api/rsvp/${guestId}/reopen`, {});
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Kunne ikke genåbne RSVP');
    }
  };

  const filtered = rsvps.filter((r) => {
    if (filter === 'attending' && !r.attending) return false;
    if (filter === 'declined' && r.attending !== false) return false;
    if (filter === 'overnight' && !r.overnight_requested) return false;
    const s = search.toLowerCase();
    return !s || (r.guest_name || '').toLowerCase().includes(s) || (r.guest_email || '').toLowerCase().includes(s);
  });

  return (
    <div className="animate-fade-in" data-testid="admin-rsvp-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-50" style={{ fontFamily: "'Outfit', sans-serif" }}>RSVP-administration</h1>
          <p className="text-sm text-slate-400 mt-1">{rsvps.length} svar</p>
        </div>
        <Button variant="outline" onClick={load} className="w-full sm:w-auto bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700" data-testid="refresh-rsvps-btn">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-4 sm:flex-wrap">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Søg efter gæst..."
          className="w-full sm:w-auto bg-slate-900/50 border border-slate-700 text-slate-100 rounded-md px-3 py-2 text-sm outline-none"
        />
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="w-full sm:w-auto bg-slate-900/50 border border-slate-700 text-slate-300 rounded-md px-3 py-2 text-sm outline-none">
          <option value="all">Alle</option>
          <option value="attending">Deltager</option>
          <option value="declined">Deltager ikke</option>
          <option value="overnight">Har brug for overnatning</option>
        </select>
      </div>

      {loading ? <p className="text-slate-400">Indlæser...</p> : (
        <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="rsvps-table">
              <thead>
                <tr className="border-b border-slate-700/50">
                  <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Gæst</th>
                  <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Deltager</th>
                  <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Gæster</th>
                  <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Kosthensyn</th>
                  <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Overnatning</th>
                  <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Status</th>
                  <th className="text-right px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Handlinger</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <tr key={r.id || r.guest_id} className="border-b border-slate-700/20 hover:bg-slate-800/30" data-testid={`rsvp-row-${r.guest_id}`}>
                    <td className="px-4 py-3 text-slate-200">{r.guest_name}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded ${r.attending ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`}>
                        {r.attending ? 'Ja' : 'Nej'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-300">{r.guest_count}</td>
                    <td className="px-4 py-3 text-slate-400 text-xs">{r.dietary_notes || '-'}</td>
                    <td className="px-4 py-3">
                      {r.overnight_requested ? (
                        <span className="text-xs px-2 py-1 bg-teal-500/10 text-teal-400 rounded">Ja</span>
                      ) : (
                        <span className="text-xs text-slate-500">Nej</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-400">{r.status}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => handleReopen(r.guest_id)} className="min-h-9 min-w-9 rounded hover:bg-slate-700 text-slate-400 hover:text-blue-400" title="Genåbn RSVP" data-testid={`reopen-rsvp-${r.guest_id}`}>
                        <RotateCcw className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filtered.length === 0 && <p className="text-center py-8 text-slate-500 text-sm">Ingen RSVP-svar fundet.</p>}
        </div>
      )}
    </div>
  );
}
